import type { InterruptEvent, InterruptGrant, Message } from './types';

/**
 * Read a card's `grant` off the wire. Anything without a non-empty `tool` and a string `label` reads
 * as not grantable (null), so an older server, or a shape this build does not know, simply shows no
 * "Approve & allow similar" button. An empty label still offers the button, only without its line.
 */
export function parseInterruptGrant(raw: unknown): InterruptGrant | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const { tool, label } = raw as { tool?: unknown; label?: unknown };
  if (typeof tool !== 'string' || !tool.trim()) return null;
  if (typeof label !== 'string') return null;
  return { tool, label };
}

/**
 * Coerce a persisted (loosely-typed) interrupt card into a full `InterruptEvent`
 * that the card components can render and the resume endpoint can route. Returns
 * null when the card is absent or lacks the tool name needed to render/route it
 * (mirrors the server-side drop rule). Missing optional fields fall back to safe
 * defaults; `conversationId` is threaded in by the caller when known (the
 * persisted card itself does not carry it).
 */
export function interruptEventFromCard(
  card: Message['interruptCard'],
  conversationId?: string | null,
): InterruptEvent | null {
  const tool = card?.action?.tool;
  if (!card || typeof tool !== 'string' || tool.length === 0) return null;
  return {
    interruptId: card.interruptId,
    agent: card.agent ?? '',
    action: { tool, args: card.action?.args ?? {} },
    description: card.description ?? '',
    allowedDecisions: card.allowedDecisions ?? ['approve', 'reject'],
    category: card.category,
    timestamp: card.timestamp,
    conversationId: conversationId ?? undefined,
    grant: card.grant ?? null,
  };
}

/**
 * The interrupt this assistant message is effectively awaiting a decision on:
 * the live SSE `pendingInterrupt` when present, or — after a reload / once the
 * broker's 5-minute replay TTL has lapsed — the persisted `interruptCard` while
 * it is still undecided (`interruptDecision == null`). Returns undefined when
 * there is no open interrupt (no card, or a decision was already consumed). This
 * is what makes a resumed-but-reloaded card actionable again: `useChat` gates
 * `submitResume` / awaiting-state on this, not on the live field alone.
 */
export function effectivePendingInterrupt(
  msg: Pick<
    Message,
    | 'pendingInterrupt'
    | 'interruptCard'
    | 'interruptDecision'
    | 'status'
    | 'interruptResolvedAt'
  >,
): InterruptEvent | undefined {
  /**
   * A decided, resolved, failed, or cancelled turn is never awaiting — even if an
   * orphan stream replay re-set the live `pendingInterrupt` on it. Bail before
   * returning the live field so `isAwaitingApproval` can't disable the
   * composer on a turn MessageBubble already renders read-only.
   */
  if (
    msg.interruptDecision != null ||
    msg.interruptResolvedAt != null ||
    msg.status === 'cancelled' ||
    msg.status === 'failed'
  ) {
    return undefined;
  }
  if (msg.pendingInterrupt) return msg.pendingInterrupt;
  /**
   * Fall back to the persisted card only while the turn is genuinely OPEN: still
   * `streaming` (decided/resolved were already excluded above). `interruptDecision`
   * is parsed with `.catch(null)`, so this guard also blocks an unknown decision
   * shape on a closed row from reading as "undecided" and re-enabling resume.
   */
  if (msg.status === 'streaming' && msg.interruptCard) {
    return interruptEventFromCard(msg.interruptCard) ?? undefined;
  }
  return undefined;
}

/**
 * The row's auto-approvals plus the one stored as its card, so dropping that card keeps its row. The
 * stored one is added once, and only when a rule approved it.
 */
function withStoredAutoApproval(message: Message): Message['autoApprovals'] {
  const recorded = message.autoApprovals;
  const decision = message.interruptDecision;
  const card = message.interruptCard;
  const tool = card?.action?.tool;
  if (decision?.type !== 'auto_approved' || !card || !tool) return recorded;
  if (recorded?.some((entry) => entry.interruptId === card.interruptId)) {
    return recorded;
  }
  return [
    ...(recorded ?? []),
    {
      interruptId: card.interruptId,
      grantId: decision.grantId,
      tool,
      description: card.description ?? '',
    },
  ];
}

/**
 * The row with a live interrupt frame applied. A frame for a different interrupt than the stored card
 * means the server replaced that card, so the stored card, its decision and its resolved marker are
 * dropped with it; otherwise a row whose earlier card was decided (or approved by a rule) would keep
 * the new card from being answered. A replayed frame of the stored card itself keeps its decision, so
 * a decided card never becomes actionable again.
 */
export function withLiveInterrupt(
  message: Message,
  interrupt: InterruptEvent,
): Message {
  if (message.interruptCard?.interruptId === interrupt.interruptId) {
    return { ...message, pendingInterrupt: interrupt };
  }
  return {
    ...message,
    autoApprovals: withStoredAutoApproval(message),
    pendingInterrupt: interrupt,
    interruptCard: null,
    interruptDecision: null,
    interruptResolvedAt: null,
  };
}

/**
 * Fresh rows with the auto-approvals this client has shown carried over by message id: the ones
 * recorded from the stream and the one its stored card held. The server stores only one per message,
 * so a history read would otherwise drop the rest; the adapter removes the stored one's duplicate.
 */
export function keepLocalAutoApprovals(
  fresh: Message[],
  current: Message[],
): Message[] {
  const recorded = new Map<string, NonNullable<Message['autoApprovals']>>();
  for (const message of current) {
    const shown = withStoredAutoApproval(message);
    if (shown?.length) recorded.set(message.id, shown);
  }
  if (recorded.size === 0) return fresh;
  return fresh.map((message) => {
    const autoApprovals = recorded.get(message.id);
    return autoApprovals ? { ...message, autoApprovals } : message;
  });
}
